'use client';

import type { Common } from '@cruz-com/lookup';
import { useDebounce } from 'ahooks';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { HiOutlineTrash } from 'react-icons/hi2';
import { ReactSortable } from 'react-sortablejs';
import { twMerge } from 'tailwind-merge';
import { myCruzApi } from '@/api';
import { Button, Confirm, CruzIcon } from '@/components';
import { useGlobalStore } from '@/store/provider';
type SortablePhoto = Common.Photo & {
  id: string | number;
};
type Props = {
  _id: string;
  photo_list: Common.Photo[];
  onPhotoDoneAction: (d: Common.Photo[]) => void;
};
export function MyVehiclePhotos({
  _id,
  photo_list,
  onPhotoDoneAction
}: Props) {
  const {
    setToast
  } = useGlobalStore(state => state);
  const mountRef = useRef<boolean>(false);
  const [photos, setPhotos] = useState<SortablePhoto[]>(photo_list as SortablePhoto[]);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    setPhotos(photo_list as SortablePhoto[]);
  }, [photo_list]);

  // select photos
  const [countSelected, setCountSelected] = useState(0);
  function toggleSelectPhoto(id: string) {
    const photo = _.find(photos, {
      id
    });
    if (!photo) return;
    photo.selected = !photo.selected;
    setPhotos(photos);
    setCountSelected(_.filter(photos, 'selected').length);
  }
  function unSelectPhotos() {
    setPhotos(_.map(photos, x => ({
      ...x,
      selected: false
    })));
    setCountSelected(0);
  }

  // confirm & delete select photos
  const [showConfirm, setShowConfirm] = useState(false);
  async function deleteSelectedPhotos() {
    setShowConfirm(false);
    setIsDeleting(true);
    try {
      const {
        data
      } = await myCruzApi.vehicle.deletePhotos(_id, _.filter(photos, 'selected').map(x_0 => _.pick(x_0, ['id'])));
      setCountSelected(0);
      onPhotoDoneAction(data);
    } catch (err) {
      setToast({
        type: 'error',
        message: _.get(err, 'message')
      });
    } finally {
      setIsDeleting(false);
    }
  }

  // sort photo
  function onSort(d: SortablePhoto[]) {
    if (mountRef.current) {
      setPhotos(d);
    }
  }

  // prevent useEffect on mount
  useEffect(() => {
    mountRef.current = true;
  }, []);

  // monitor sort
  const debouncedValue = useDebounce(photos, {
    wait: 500
  });
  useEffect(() => {
    if (mountRef.current) {
      if (countSelected === 0 && !_.isEqual(_.map(debouncedValue, 'id'), _.map(photo_list, 'id'))) {
        saveSortedPhotos();
      }
    }
  }, [debouncedValue]);

  // save sorted photos
  async function saveSortedPhotos() {
    try {
      const {
        data: data_0
      } = await myCruzApi.vehicle.sortPhotos(_id, _.map(photos, x_1 => _.pick(x_1, ['id'])));
      onPhotoDoneAction(data_0);
    } catch (err_0) {
      setToast({
        type: 'error',
        message: _.get(err_0, 'message')
      });
    }
  }
  if (_.isEmpty(photos)) {
    return null;
  }
  return <section aria-label='Vehicle Photos Management'>
      <div className='my-4 w-full rounded-md border p-4'>
        <Confirm title='Please Confirm' content='Are you sure you want to delete selected photo(s)?' open={showConfirm} onConfirm={() => {
        deleteSelectedPhotos();
      }} onCancel={() => {
        setShowConfirm(false);
      }} />
        <ReactSortable list={photos} setList={d_0 => onSort(d_0 as SortablePhoto[])} animation={200} className='m-0 grid w-full list-none grid-cols-4 items-center gap-2 p-0 md:grid-cols-3 xl:grid-cols-4' tag='ul'>
          {_.map(photos, x_2 => {
          return <li key={x_2.id as string}>
                <button type='button' onClick={() => toggleSelectPhoto(x_2.id as string)} aria-pressed={x_2.selected} aria-label={`Toggle selection for photo ${x_2.id}`} className={twMerge('w-full rounded-md border py-1', x_2.selected ? 'bg-red-800' : 'bg-zinc-100 dark:bg-zinc-700')}>
                  <img alt={`Vehicle ${x_2.id}`} src={x_2.p_url} className='m-auto aspect-16/10 object-contain' />
                </button>
              </li>;
        })}
        </ReactSortable>
        {countSelected > 0 && <div className='mt-6 flex w-full items-center justify-center gap-x-2'>
            <Button type='button' variant='outline' onClick={unSelectPhotos} className='h-10 px-5' aria-label='Cancel bulk selection'>
              Cancel
            </Button>
            <Button type='button' variant='secondary' className='h-10 px-5' onClick={() => setShowConfirm(true)} working={isDeleting} aria-label={`Delete ${countSelected} selected photos`}>
              <HiOutlineTrash className='mr-1 -ml-1 size-4' aria-hidden='true' />
              Delete {countSelected} Photo{countSelected !== 1 && 's'}
            </Button>
          </div>}
      </div>
      {
    // Help Text
    _.size(photos) > 0 && <aside className='mt-4 flex gap-x-4' aria-label='Tips'>
            <CruzIcon.Tips className='mt-1 size-6' aria-hidden='true' />
            <ul className='m-0 w-full list-inside list-disc p-0 text-sm/6'>
              <li>Drag and drop photos to reorder them</li>
              <li>To delete photos, select them and click the "Delete" button</li>
            </ul>
          </aside>}
    </section>;
}